/**
 * GlitchReplay CSP-violation capture — local integration.
 *
 * Listens for the browser's `securitypolicyviolation` event and pipes each
 * Content-Security-Policy block through the Sentry SDK's capture path so it
 * lands in GlitchReplay alongside JS errors and a11y findings. The site's
 * CSP carries no `report-uri`/`report-to` directive and Sentry has no
 * native CSP instrumentation, so without this a blocked script / style /
 * connect is invisible beyond the user's own console.
 *
 * Each violation becomes a regular event tagged
 * `glitchreplay.event_type=csp_violation` — the GlitchReplay consumer can
 * route that tag to a dedicated dashboard view, the same way
 * `a11y_violation` is routed to the Accessibility tab.
 *
 * Deliberately mirrors the functional-integration shape of
 * `@glitchreplay/a11y` and `@glitchreplay/network-probe` (self-contained,
 * loose `SentryClient`/`SentryIntegration` interfaces, `console.warn`
 * fallback) so it can be lifted into a published `@glitchreplay/csp`
 * package later with no behaviour change — the "local now, extract later"
 * decision. Built for the Sentry SDK v8+ functional integration shape.
 */

/** Minimal slice of the Sentry client this integration touches. */
interface SentryClient {
  captureEvent(event: Record<string, unknown>): string;
}

/** Functional-integration shape Sentry calls into. */
interface SentryIntegration {
  name: string;
  setupOnce: () => void;
  setup?: (client: SentryClient) => void;
}

export interface CspIntegrationOptions {
  /**
   * Capture `disposition: "report"` violations (i.e. those from a
   * `Content-Security-Policy-Report-Only` header). Default true —
   * report-only is exactly how a tighter policy gets staged, so those
   * violations are the ones most worth seeing before they bite.
   */
  includeReportOnly?: boolean;
  /**
   * `blockedURI` / `sourceFile` URL schemes treated as noise and dropped.
   * Browser extensions inject scripts and styles into every page and trip
   * CSP constantly; none of it is actionable. Default covers Chrome,
   * Firefox and Safari extension schemes plus Safari's masked-URL form.
   */
  ignoreSchemes?: ReadonlyArray<string>;
  /** `effectiveDirective` values to skip entirely (project allowlisting). */
  ignoreDirectives?: ReadonlyArray<string>;
  /**
   * Hosts treated as noise regardless of scheme. See DEFAULT_IGNORE_HOSTS.
   * Matched on the exact host or any subdomain of it.
   */
  ignoreHosts?: ReadonlyArray<string>;
  /**
   * Called for every violation before capture. Return false to skip the
   * report — use for project-level allowlisting of known-benign blocks.
   */
  beforeCapture?: (violation: SecurityPolicyViolationEvent) => boolean;
}

// Extension content scripts run on every page and routinely violate the
// host's CSP (injecting their own inline styles, loading their own assets).
// The block is real but never the site owner's bug — filter on scheme.
/*
 * Hosts that belong to the VISITOR's environment, not to this site.
 *
 * Scheme filtering catches browser extensions, but corporate network controls
 * serve their interstitials over plain https and therefore sail through it.
 * The observed case: `blocked.teams.cloudflare.com`, Cloudflare Zero Trust's
 * Gateway block page, which appears when a reader's own employer blocks a
 * resource. That is a report about their network, not a defect here, and
 * "fixing" it would mean allowlisting a corporate filtering product to frame
 * our pages — which achieves nothing and weakens the policy.
 *
 * Only add a host here when a block by it can never be our bug.
 */
const DEFAULT_IGNORE_HOSTS = [
  // Cloudflare Zero Trust / Gateway block pages.
  "blocked.teams.cloudflare.com",
  // Cisco Umbrella block page.
  "block.opendns.com",
  "id.opendns.com",
  // Zscaler and Netskope interstitials.
  "gateway.zscaler.net",
  "gateway.zscalerthree.net",
  "notify.netskope.com",
  // Forcepoint / Websense block pages.
  "blockpage.forcepoint.com",
  // PX Center corporate navigation block page.
  "bloqueado.px.center",
] as const;

const DEFAULT_IGNORE_SCHEMES = [
  "chrome-extension",
  "moz-extension",
  "safari-extension",
  "safari-web-extension",
  "webkit-masked-url",
  "ms-browser-extension",
];

function schemeOf(uri: string): string {
  const m = /^([a-z][a-z0-9+.-]*):/i.exec(uri);
  return m ? m[1].toLowerCase() : "";
}

/**
 * `blockedURI` is either a CSP keyword (`inline`, `eval`, `self`, …) or a
 * URL. For URLs we key on the host so cache-busted query strings and path
 * variants don't shard one misconfiguration into many separate issues.
 */
function blockedKey(blockedURI: string): string {
  if (!blockedURI) return "(empty)";
  try {
    return new URL(blockedURI).host || blockedURI;
  } catch {
    return blockedURI;
  }
}

/**
 * Build a Sentry integration that reports CSP violations to GlitchReplay.
 * Pass into `Sentry.init({ integrations: [...] })`.
 */
export function cspIntegration(options: CspIntegrationOptions = {}): SentryIntegration {
  const includeReportOnly = options.includeReportOnly !== false;
  const ignoreSchemes = new Set(
    (options.ignoreSchemes ?? DEFAULT_IGNORE_SCHEMES).map((s) => s.toLowerCase()),
  );
  const ignoreDirectives = new Set(options.ignoreDirectives ?? []);
  const ignoreHosts = new Set(
    (options.ignoreHosts ?? DEFAULT_IGNORE_HOSTS).map((h) => h.toLowerCase()),
  );

  /** True when a host is, or sits under, an ignored host. */
  const isIgnoredHost = (uri: string): boolean => {
    if (!uri) return false;
    let host: string;
    try {
      host = new URL(uri).host.toLowerCase();
    } catch {
      return false;
    }
    if (!host) return false;
    if (ignoreHosts.has(host)) return true;
    for (const ignored of ignoreHosts) {
      if (host.endsWith("." + ignored)) return true;
    }
    return false;
  };
  let client: SentryClient | null = null;
  // Dedup within a page load — a single misconfigured directive can fire
  // the event many times over (one per blocked resource or re-render).
  const seen = new Set<string>();

  const capture = (
    event: SecurityPolicyViolationEvent,
    directive: string,
    key: string,
    fingerprint: string[],
  ) => {
    const grEvent = {
      message: `CSP: ${directive} blocked ${key}`.slice(0, 300),
      // An enforced block broke something on the page; a report-only block
      // is advance warning of a policy that isn't live yet.
      level: event.disposition === "report" ? "info" : "warning",
      tags: {
        "glitchreplay.event_type": "csp_violation",
        "glitchreplay.csp_directive": directive,
        "glitchreplay.csp_disposition": event.disposition || "enforce",
        "glitchreplay.csp_blocked": key,
      },
      contexts: {
        csp: {
          blocked_uri: event.blockedURI,
          document_uri: event.documentURI,
          violated_directive: event.violatedDirective,
          effective_directive: event.effectiveDirective,
          disposition: event.disposition,
          source_file: event.sourceFile || undefined,
          line_number: event.lineNumber || undefined,
          column_number: event.columnNumber || undefined,
          status_code: event.statusCode,
          // Offending snippet — only populated when the policy carries
          // `'report-sample'`. Truncated; can contain page content.
          sample: event.sample ? String(event.sample).slice(0, 200) : undefined,
          original_policy: event.originalPolicy
            ? event.originalPolicy.slice(0, 1000)
            : undefined,
        },
      },
      fingerprint,
      platform: "javascript",
      timestamp: Date.now() / 1000,
    };
    if (client) {
      client.captureEvent(grEvent);
    } else if (typeof console !== "undefined") {
      // Dev-friendly fallback when no SDK has wired us up.
      console.warn("[glitchreplay/csp]", grEvent.message);
    }
  };

  const handler = (event: SecurityPolicyViolationEvent) => {
    try {
      const directive = event.effectiveDirective || event.violatedDirective || "";
      if (ignoreDirectives.has(directive)) return;
      if (event.disposition === "report" && !includeReportOnly) return;

      // Extension-injected noise: the block is on an extension resource
      // (blockedURI) or on inline code an extension injected (sourceFile).
      if (ignoreSchemes.has(schemeOf(event.blockedURI))) return;
      if (ignoreSchemes.has(schemeOf(event.sourceFile || ""))) return;

      // Corporate network controls block over https, so scheme filtering above
      // does not catch them. These reports describe the reader's network.
      if (isIgnoredHost(event.blockedURI)) return;
      if (isIgnoredHost(event.sourceFile || "")) return;

      if (options.beforeCapture && options.beforeCapture(event) === false) return;

      const key = blockedKey(event.blockedURI);
      const fingerprint = ["csp", directive, key];
      const dedup = fingerprint.join("|");
      if (seen.has(dedup)) return;
      seen.add(dedup);

      capture(event, directive, key, fingerprint);
    } catch {
      // An error handler must never throw.
    }
  };

  return {
    name: "glitchReplayCsp",
    setupOnce() {
      // Sentry v8 calls this once globally; setup() runs per-client.
    },
    setup(c) {
      client = c;
      if (typeof document === "undefined") return;
      document.addEventListener("securitypolicyviolation", handler);
    },
  };
}
